import { useRef } from "react";

import { formatMoney, formatNumber } from "./licensingTheme";
import { useBarsRise } from "./licensingMotion";
import { ChartTooltip, useChartTooltip } from "./ChartTooltip";

const DAY = 86400000;

/**
 * The next thirty days of subscription terms coming to an end.
 *
 * Every paying subscription has a term that ends on a date. On that date one
 * of two things happens: auto-renew is on, so the card is charged and the term
 * rolls over (green); or it is off, so the subscription ends (amber). This is
 * the calendar of both, with the money the green ones will bring in.
 */
export default function RenewalRunway({ runway, currency = "USD", loading, onDrill }) {
  const scope = useRef(null);
  const { tip, bind, hide } = useChartTooltip();
  const rows = runway ?? [];

  const start = new Date();
  start.setUTCHours(0, 0, 0, 0);
  const days = Array.from({ length: 30 }, (_, i) => {
    const date = new Date(start.getTime() + i * DAY);
    const key = date.toISOString().slice(0, 10);
    const row = rows.find((r) => r.day === key);
    return {
      key,
      date,
      renewing: row ? Number(row.renewing) || 0 : 0,
      ending: row ? Number(row.ending) || 0 : 0,
      atStakeCents: row ? Number(row.atStakeCents) || 0 : 0,
    };
  });

  const max = Math.max(1, ...days.map((d) => d.renewing + d.ending));
  const renewing = days.reduce((sum, d) => sum + d.renewing, 0);
  const ending = days.reduce((sum, d) => sum + d.ending, 0);
  const due = days.reduce((sum, d) => sum + d.atStakeCents, 0);
  const label = (date, opts = {}) =>
    date.toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC", ...opts });

  useBarsRise(scope, [JSON.stringify(rows)]);

  return (
    <section className="mcap-lic-panel" aria-labelledby="mcap-lic-runway-title" ref={scope}>
      <header className="mcap-lic-panel-head">
        <div>
          <h3 id="mcap-lic-runway-title" className="mcap-lic-panel-title">
            Next 30 days
          </h3>
          <p className="mcap-lic-panel-sub">
            Paid plans reaching their renewal date, and whether each will renew.
          </p>
        </div>
      </header>

      <div className="mcap-lic-stat-strip">
        <button
          type="button"
          className="mcap-lic-stat is-good"
          disabled={!renewing}
          onClick={() => onDrill({ type: "cohort", title: "Renewing in the next 30 days", filters: { renewsWithinDays: "30", autoRenew: "true" } })}
        >
          <span className="mcap-lic-stat-value">{loading ? "—" : formatNumber(renewing)}</span>
          <span className="mcap-lic-stat-label">Will renew</span>
        </button>
        <div className="mcap-lic-stat is-money">
          <span className="mcap-lic-stat-value">{loading ? "—" : formatMoney(due, currency)}</span>
          <span className="mcap-lic-stat-label">Expected income</span>
        </div>
        <button
          type="button"
          className="mcap-lic-stat is-warn"
          disabled={!ending}
          onClick={() => onDrill({ type: "cohort", title: "Won't renew in the next 30 days", filters: { renewsWithinDays: "30", autoRenew: "false" } })}
        >
          <span className="mcap-lic-stat-value">{loading ? "—" : formatNumber(ending)}</span>
          <span className="mcap-lic-stat-label">Won't renew (auto-renew off)</span>
        </button>
      </div>

      <div className="mcap-lic-bars is-runway">
        {days.map((entry, index) => {
          const total = entry.renewing + entry.ending;
          const monday = entry.date.getUTCDay() === 1;
          return (
            <button
              key={entry.key}
              type="button"
              className={`mcap-lic-bar-col ${total ? "" : "is-empty"} ${index === 0 ? "is-today" : ""}`}
              {...bind({
                title: label(entry.date, { weekday: "long" }),
                subtitle: index === 0 ? "Today" : `In ${index} day${index === 1 ? "" : "s"}`,
                rows: total
                  ? [
                      { label: "Will renew", value: formatNumber(entry.renewing), color: "#227f5d" },
                      { label: "Won't renew", value: formatNumber(entry.ending), color: "#c9972b" },
                      { label: "Expected income", value: formatMoney(entry.atStakeCents, currency) },
                    ]
                  : [{ label: "Renewals", value: "None", muted: true }],
                footer: total ? "Click to see who" : undefined,
              })}
              onClick={() => {
                if (!total) return;
                hide();
                onDrill({ type: "cohort", title: `Renewal date ${label(entry.date)}`, filters: { renewsOn: entry.key } });
              }}
              aria-label={`${label(entry.date)}: ${entry.renewing} renewing, ${entry.ending} ending`}
            >
              <span className="mcap-lic-bar-value">{total || ""}</span>
              <span className="mcap-lic-bar-track">
                {total > 0 && (
                  <span className="mcap-lic-bar-stack" data-bar style={{ height: `${Math.max(10, (total / max) * 100)}%` }}>
                    {entry.ending > 0 && <span className="is-ending" style={{ flex: entry.ending }} />}
                    {entry.renewing > 0 && <span className="is-renewing" style={{ flex: entry.renewing }} />}
                  </span>
                )}
              </span>
              <span className="mcap-lic-bar-label">
                {index === 0
                  ? "Today"
                  : // Mondays mark the weeks; the last day is labelled only when
                    // no Monday label sits close enough to collide with it.
                    (monday && index > 2) || (index === 29 && days.slice(26, 29).every((d) => d.date.getUTCDay() !== 1))
                    ? label(entry.date)
                    : ""}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mcap-lic-axis-note">
        <span className="mcap-lic-legend-item">
          <span className="mcap-lic-legend-dot" style={{ background: "#227f5d" }} aria-hidden="true" /> Renews
        </span>
        <span className="mcap-lic-legend-item">
          <span className="mcap-lic-legend-dot is-hatched" aria-hidden="true" /> Won't renew
        </span>
      </div>

      {!loading && renewing + ending === 0 && (
        <p className="mcap-lic-empty">No paid plan is due for renewal in the next 30 days.</p>
      )}

      <ChartTooltip tip={tip} />
    </section>
  );
}
